// Upgrade-upsell modal. Restyle of legacy showUpgradeModal + _upgradeFeatures
// (snapshot L3654-3720 logic, L9581-9602 markup). Shown when a free user hits
// a gated feature; `reason` keys into the feature copy table.
import { el, openModal, closeModal } from './../ui/dom'
import { state } from './../state'
import { openAuth } from './../auth/auth-ui'

type Tier = 'pro' | 'business'

interface UpgradeFeature {
  icon: string
  title: string
  desc: string
  tier: Tier
}

// Verbatim copy + inline SVGs from the live _upgradeFeatures table.
const FEATURES: Record<string, UpgradeFeature> = {
  custom_sizes: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="2" viewBox="0 0 24 24"><path d="M21 3H3v18h18V3zM9 3v18M3 9h18"/></svg>',
    title: 'Unlock Custom Sizes',
    desc: 'Create labels in any dimension you need — perfect for unique packaging, products, or printers.',
    tier: 'pro',
  },
  image_design: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>',
    title: 'Unlock Design on Image',
    desc: 'Use your own photo or image as the canvas and add text, barcodes, and graphics on top.',
    tier: 'pro',
  },
  batch_labels: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="2" viewBox="0 0 24 24"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/></svg>',
    title: 'Batch Import Limit Reached',
    desc: "You've used all 5 free batch imports this month. Start a free 7-day Pro trial for unlimited imports.",
    tier: 'pro',
  },
  zpl_export: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="2" viewBox="0 0 24 24"><rect x="6" y="2" width="12" height="20" rx="2"/><path d="M6 7h12M6 17h12M10 12h4"/></svg>',
    title: 'Unlock ZPL Export',
    desc: 'Export labels in ZPL format for direct printing on Zebra and other thermal printers.',
    tier: 'business',
  },
  ad_free: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="2" viewBox="0 0 24 24"><path d="M12 2l2.4 6.9H22l-5.8 4.5 2.2 7.1L12 16l-6.4 4.5 2.2-7.1L2 8.9h7.6z"/></svg>',
    title: 'Go Ad-Free with Pro',
    desc: 'Print and export without ads or waiting — plus custom sizes, batch labels, sheet export, and more.',
    tier: 'pro',
  },
  sheet_export: {
    icon: '<svg width="24" height="24" fill="none" stroke="#3b82f6" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>',
    title: 'Unlock Sheet Export',
    desc: 'Export full label sheets for Avery, A4, and US Letter — ready for your laser or inkjet printer.',
    tier: 'pro',
  },
}

export function openUpgrade(reason = 'custom_sizes'): void {
  const feat = FEATURES[reason] ?? FEATURES.custom_sizes
  const loggedOut = !state.currentUser

  const wrap = el('div')

  // Blue accent top bar.
  const bar = el('div')
  bar.style.cssText = 'height:4px;margin:-24px -24px 0;background:linear-gradient(90deg,#3b82f6,#6366f1)'
  wrap.append(bar)

  const pad = el('div')
  pad.style.cssText = 'padding:28px 8px 4px'

  // Icon badge.
  const iconBadge = el('div')
  iconBadge.style.cssText = 'width:48px;height:48px;border-radius:12px;background:rgba(59,130,246,0.1);display:flex;align-items:center;justify-content:center;margin:0 auto 16px'
  iconBadge.innerHTML = feat.icon
  pad.append(iconBadge)

  const title = el('h3', 'text-lg font-bold text-white text-center', feat.title)
  title.style.marginBottom = '8px'
  pad.append(title)

  const desc = el('p', 'text-sm text-slate-400 text-center', feat.desc)
  desc.style.cssText = 'margin-bottom:24px;line-height:1.5'
  pad.append(desc)

  // Primary CTA.
  const cta = el('button', 'w-full py-2.5 rounded-lg font-semibold text-sm transition-all') as HTMLButtonElement
  cta.type = 'button'
  cta.style.cssText = 'background:#3b82f6;color:white'
  cta.addEventListener('mouseover', () => { cta.style.background = '#2563eb' })
  cta.addEventListener('mouseout', () => { cta.style.background = '#3b82f6' })
  if (feat.tier === 'business') {
    cta.textContent = loggedOut ? 'Start 7-day free trial' : 'Upgrade to Business — $12/mo'
  } else {
    cta.textContent = loggedOut ? 'Start 7-day free trial' : 'Upgrade to Pro — $3/mo'
  }
  cta.addEventListener('click', () => {
    closeModal()
    if (loggedOut) openAuth()
    else window.open('/account/', '_blank')
  })
  pad.append(cta)

  // Secondary dismiss.
  const dismiss = el('button', 'w-full py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors mt-2', 'Maybe later')
  dismiss.type = 'button'
  dismiss.addEventListener('click', closeModal)
  pad.append(dismiss)

  // Compare all plans link.
  const linkRow = el('div', 'text-center mt-3')
  const link = el('a', 'text-xs text-slate-500 hover:text-blue-400 transition-colors', 'Compare all plans')
  link.href = '/pricing/'
  link.target = '_blank'
  link.style.textDecoration = 'underline'
  linkRow.append(link)
  pad.append(linkRow)

  wrap.append(pad)

  openModal({ title: feat.title, body: wrap, width: '420px', testid: 'upgrade-modal' })
}
